import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { 
  Play, 
  Download, 
  Loader2, 
  Trash2, 
  MoreVertical,
  AlertTriangle,
  Lock,
  Globe,
  Eye,
  Crown
} from 'lucide-react'
import { convertTimestamp } from "@/utils/convertTimestamp"
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

// 使用动态导入避免循环依赖
const PricingModal = lazy(() => import('@/components/modals/PricingModal'))

export function VideoCardEnhanced({ 
  item, 
  isProcessing, 
  hasAccess = false, 
  showLoading = true, 
  onViewDetails,
  onDelete,
  onToggleVisibility,
  isOwner = false // 新增：是否为视频拥有者
}) {
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false)
  const [showPricingModal, setShowPricingModal] = useState(false)
  
  const t = useTranslations('videocard')
  
  const [playingVideo, setPlayingVideo] = useState(false)
  const [videoData, setVideoData] = useState({
    url: '',
    originalUrl: '',
    waterMarkedUrl: '',
    prompt: '',
    image_url: undefined
  })
  const [isImage, setIsImage] = useState(false) // 标记是否为图片

  const [videoAspect, setVideoAspect] = useState('landscape')
  const videoRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const actionsMenuRef = useRef(null)

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setShowActionsMenu(false)
      }
    }
    
    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showActionsMenu])

  // 视频数据处理逻辑 - 统一与 MediaDetailsModal 一致
  useEffect(() => {
    try {
      let data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data

      let originalUrl = ''
      let waterMarkedUrl = ''
      let isImageContent = false // 标记是否为图片内容

      const videoType = item.originalType || item.type

      // 优先从 output 字段解析
      if (item.output) {
        let outputData = null

        // 如果 output 是字符串，先解析为对象
        if (typeof item.output === 'string') {
          try {
            outputData = JSON.parse(item.output)
          } catch (e) {
            console.warn('Failed to parse output JSON:', e)
            // 如果解析失败，可能是直接的 URL 字符串
            originalUrl = item.output
            outputData = null
          }
        } else if (typeof item.output === 'object') {
          outputData = item.output
        }

        // 根据不同的 output 结构提取 URL，并判断是否为图片
        if (outputData) {
          // 1. WAN22 图片格式: { image: { url, stored_url, original_url } }
          if (outputData.image && outputData.image.url) {
            originalUrl = outputData.image.url || outputData.image.stored_url
            waterMarkedUrl = originalUrl
            isImageContent = true // 这是图片
          }
          // 2. WAN22 视频格式: { video: { url, stored_url, original_url } }
          else if (outputData.video && outputData.video.url) {
            originalUrl = outputData.video.url || outputData.video.stored_url
            waterMarkedUrl = originalUrl
            isImageContent = false // 这是视频
          }
          // 3. Veo3 / Veo3-HF 格式: { url, waterMarkedUrl, originalUrl }
          else if (outputData.url && outputData.waterMarkedUrl) {
            originalUrl = outputData.url // R2 不带水印
            waterMarkedUrl = outputData.waterMarkedUrl // R2 带水印
            isImageContent = false // 这是视频
          }
          // 4. 简单格式: { url }
          else if (outputData.url) {
            originalUrl = outputData.url
            waterMarkedUrl = originalUrl
            isImageContent = false // 默认视频
          }
          // 5. 错误格式: { error }
          else if (outputData.error) {
            console.warn('Output contains error:', outputData.error)
          }
        }
      }

      // 如果 output 没有解析到 URL，尝试从 data 字段获取（向后兼容）
      if (!originalUrl && !waterMarkedUrl && data) {
        waterMarkedUrl = data.waterMarkedUrl || data.watermarked_url || data.result?.waterMarkedUrl || ''
        originalUrl = data.url || data.video_url || data.original_url || data.result?.url || ''
      }

      // 根据用户权限决定显示哪个 URL
      const displayUrl = hasAccess ? originalUrl : (waterMarkedUrl || originalUrl)

      // 解析 input 获取 prompt
      let prompt = ''
      if (item.input) {
        let inputData = null
        if (typeof item.input === 'string') {
          try {
            inputData = JSON.parse(item.input)
          } catch (e) {
            console.warn('Failed to parse input JSON:', e)
            prompt = item.input // 如果解析失败，可能是直接的 prompt 字符串
          }
        } else if (typeof item.input === 'object') {
          inputData = item.input
        }

        if (inputData) {
          prompt = inputData.prompt || inputData.text || inputData.description || ''
        }
      }

      // Fallback: 从其他字段获取 prompt
      if (!prompt) {
        prompt = data?.prompt || item.prompt || ''
      }

      // 调试日志 - 显示完整 URL 用于调试
      console.log('🎬 VideoCardEnhanced - 数据解析结果:', {
        taskId: item.task_id,
        videoType,
        status: item.status,
        isImageContent, // 显示是否为图片
        // URL 相关
        hasOutput: !!item.output,
        outputType: typeof item.output,
        outputData: typeof item.output === 'object' ? item.output : null, // 完整显示对象
        originalUrl: originalUrl || '(empty)', // 显示完整 URL
        waterMarkedUrl: waterMarkedUrl || '(empty)', // 显示完整 URL
        displayUrl: displayUrl || '(empty)', // 显示完整 URL
        hasAccess,
        outputPreview: typeof item.output === 'string' ? item.output.substring(0, 150) : null,
        // Prompt 相关
        hasInput: !!item.input,
        inputType: typeof item.input,
        prompt: prompt ? prompt.substring(0, 50) + '...' : '(empty)',
        inputPreview: typeof item.input === 'string' ? item.input.substring(0, 100) : item.input
      })

      // 设置是否为图片
      setIsImage(isImageContent)

      setVideoData({
        url: displayUrl,
        originalUrl: originalUrl,
        waterMarkedUrl: waterMarkedUrl || originalUrl,
        prompt: prompt,
        image_url: data?.image_url || item.image_url
      })
      
      if (!displayUrl) {
        setIsLoading(true)
        setHasError(false)
      } else {
        // 如果有URL，先保持加载状态，等视频元数据加载完成后再设置为false
        setIsLoading(true)
        setHasError(false)
      }
    } catch (error) {
      console.error('Error parsing video data:', error)
      toast.error(t('errorLoadingData'))
      setIsLoading(false)
    }
  }, [item.data, item.output, item.input, item.originalType, item.type, item.prompt, hasAccess, t])

  const handleVideoLoaded = (e) => {
    const video = e.target
    if (video) {
      const ratio = video.videoWidth / video.videoHeight
      if (ratio > 1.2) {
        setVideoAspect('landscape')
      } else if (ratio < 0.8) {
        setVideoAspect('portrait')
      } else {
        setVideoAspect('square')
      }
      console.log('✅ 视频加载成功:', item.task_id, videoData.url)
      setIsLoading(false)
      setHasError(false)
    }
  }

  const handleVideoError = (e) => {
    console.error('❌ 视频加载失败:', item.task_id, videoData.url, e)
    setIsLoading(false)
    setHasError(true)
  }

  // 删除确认处理
  const handleDeleteConfirm = async () => {
    if (!onDelete) return
    
    setIsDeleting(true)
    try {
      await onDelete(item.task_id)
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error(t('deleteVideoFailed'))
    } finally {
      setIsDeleting(false)
    }
  }

  // 可见性切换处理
  const handleVisibilityConfirm = async () => {
    if (!onToggleVisibility) return
    
    setIsTogglingVisibility(true)
    try {
      await onToggleVisibility(item.task_id, !item.is_private)
      setShowVisibilityConfirm(false)
    } catch (error) {
      console.error('Visibility toggle failed:', error)
      toast.error(t('visibilityUpdateFailed'))
    } finally {
      setIsTogglingVisibility(false)
    }
  }

  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center h-full py-6 bg-black/30">
      <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-2 border-green-400 border-t-transparent mb-3" />
      <p className="text-xs sm:text-sm text-green-400 font-medium">{t('loadingVideo')}</p>
    </div>
  )

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center h-full py-6 bg-black/30">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-500/20 flex items-center justify-center mb-3">
        <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <p className="text-xs sm:text-sm text-red-400 font-medium">Video loading failed</p>
    </div>
  )

  return (
    <div className="w-full h-full p-[1px] min-h-[220px] rounded-lg bg-gradient-to-br from-green-400/30 via-transparent to-cyan-400/30">
      <div 
        className="relative flex flex-col bg-black/80 backdrop-blur-md h-full rounded-lg overflow-hidden border border-white/5 hover:border-green-400/40 transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.2)] group"
      >
        
        {/* 顶部状态和操作栏 - 响应式优化 */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 right-2 sm:right-3 z-[]">
          <div className="flex justify-between items-start">
            {/* 左侧：可见性状态 */}
            <div className="flex items-center">
              {item.is_private && (
                <div className="px-3 sm:px-3 py-1.5 sm:py-1.5 rounded-lg sm:rounded-lg bg-black/90 border border-orange-500/50 backdrop-blur-sm shadow-lg">
                  <div className="flex items-center">
                    <Lock className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-orange-400 mr-1.5 sm:mr-1.5" />
                    <span className="text-xs sm:text-xs font-semibold text-orange-400">{t('private')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* 右侧：处理状态和操作菜单 */}
            <div className="flex items-center gap-2">

            {/* 处理中状态 */}
            {isProcessing && (
              <div className="px-3 sm:px-3 py-1.5 sm:py-1.5 rounded-lg sm:rounded-lg bg-black/90 border border-green-400/50 backdrop-blur-sm shadow-lg">
                <div className="flex items-center">
                  <div className="w-2.5 h-2.5 sm:w-2.5 sm:h-2.5 rounded-full bg-green-400 mr-2 sm:mr-2 animate-pulse" />
                  <span className="text-xs sm:text-xs font-semibold text-green-400">{t('processing')}</span>
                </div>
              </div>
            )}

            {/* 操作菜单 - 响应式优化 */}
            {isOwner && !isProcessing && (
              <div className="relative" ref={actionsMenuRef}>
                <button 
                  className="p-2 sm:p-2 rounded-lg bg-black/80 hover:bg-black/90 backdrop-blur-sm text-white border border-white/20 hover:border-green-400/40 transition-all duration-200 touch-manipulation min-w-[36px] min-h-[36px] sm:min-w-[32px] sm:min-h-[32px] flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowActionsMenu(!showActionsMenu)
                  }}
                  aria-label={t('moreActions')}
                >
                  <MoreVertical className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </button>

                {/* 下拉菜单 - 移动端优化 */}
                {showActionsMenu && (
                  <>
                    {/* 背景遮罩 - 移动端友好 */}
                    <div 
                      className="fixed inset-0 z-40 bg-transparent"
                      onClick={() => setShowActionsMenu(false)}
                    />
                    
                    <div className="absolute top-full right-0 mt-2 w-48 sm:w-52 bg-black/95 backdrop-blur-md border border-green-400/20 rounded-xl shadow-2xl shadow-green-400/10 z-50 overflow-hidden">
                                          {/* 可见性切换 */}
                      <button
                        className="w-full px-4 sm:px-5 py-3.5 sm:py-4 text-left text-sm sm:text-base text-white hover:bg-green-400/10 transition-colors flex items-center gap-3 sm:gap-4 touch-manipulation min-h-[52px]"
                        title={!item.is_private && !hasAccess ? t('upgradeForPrivate') : undefined}
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowActionsMenu(false)
                          
                          // 检查权限：如果要设置为私密且用户没有权限，显示定价模态框
                          if (!item.is_private && !hasAccess) {
                            setShowPricingModal(true)
                            return
                          }
                          
                          setShowVisibilityConfirm(true)
                        }}
                      >
                        {item.is_private ? (
                          <>
                            <Globe className="w-4 h-4 text-green-400" />
                            <span>{t('makePublic')}</span>
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4 text-orange-400" />
                            <span>{t('makePrivate')}</span>
                            {!hasAccess && (
                              <Crown className="w-3 h-3 text-yellow-400 ml-auto" />
                            )}
                          </>
                        )}
                      </button>

                      {/* 分割线 */}
                      <div className="border-t border-white/10" />

                      {/* 删除选项 */}
                      <button
                        className="w-full px-4 sm:px-5 py-3.5 sm:py-4 text-left text-sm sm:text-base text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3 sm:gap-4 touch-manipulation min-h-[52px]"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowActionsMenu(false)
                          setShowDeleteConfirm(true)
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>{t('deleteVideo')}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* 视频容器 - 使用16:9比例 */}
        <div
          className="relative size-full aspect-video bg-black/40 group overflow-hidden rounded-lg"
        >
          {/* 详情查看按钮 - 移至左下角避免与顶部按钮重叠 */}
          <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 opacity-90 sm:opacity-0 group-hover:opacity-100 transition-all duration-300 z-30">
            <button 
              className="p-2 sm:p-2 rounded-lg sm:rounded-lg bg-black/80 hover:bg-black/90 backdrop-blur-sm text-white transition-all duration-300 shadow-lg hover:shadow-green-400/20 border border-transparent hover:border-green-400/30 touch-manipulation min-w-[36px] min-h-[36px] sm:min-w-[32px] sm:min-h-[32px] flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation()
                if (onViewDetails) {
                  onViewDetails(item)
                }
              }}
              title={t('viewDetails')}
              aria-label={t('viewVideoDetails')}
            >
              <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
          </div>
          
          
          {!videoData.url ? (
            isProcessing ? renderLoadingState() : renderErrorState()
          ) : hasError ? (
            renderErrorState()
          ) : isImage ? (
            // 显示图片（根据 output 数据判断）
            <>
              <img
                src={videoData.url}
                alt={videoData.prompt}
                className="absolute inset-0 w-full h-full object-contain rounded-lg"
                onLoad={() => {
                  console.log('✅ 图片加载成功:', item.task_id, videoData.url)
                  setIsLoading(false)
                  setHasError(false)
                }}
                onError={(e) => {
                  console.error('❌ 图片加载失败:', item.task_id, videoData.url, e)
                  setIsLoading(false)
                  setHasError(true)
                }}
                style={{ zIndex: 1 }}
              />

              {/* 图片加载状态 */}
              {isLoading && videoData.url && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70" style={{ zIndex: 20 }}>
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-2 border-green-400 border-t-transparent mb-2" />
                    <p className="text-xs sm:text-sm text-green-400">Loading image...</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            // 显示视频
            <>
              <video
                ref={videoRef}
                onLoadedMetadata={handleVideoLoaded}
                onError={handleVideoError}
                src={videoData.url}
                poster={videoData.image_url}
                controls={playingVideo}
                className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 rounded-lg"
                style={{ backgroundColor: '#000', zIndex: 1 }}
                loop
                playsInline
                preload="metadata"
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                disablePictureInPicture
                disableRemotePlayback
                onClick={(e) => {
                  if (!playingVideo) {
                    e.preventDefault()
                    e.stopPropagation()
                    setPlayingVideo(true)
                    e.target.play()
                  }
                }}
              />

              {/* 封面图片 */}
              {!playingVideo && videoData.image_url && (
                <img
                  src={videoData.image_url}
                  alt={videoData.prompt}
                  className="absolute inset-0 w-full h-full object-contain rounded-lg"
                  style={{ zIndex: 5 }}
                />
              )}

              {/* 播放按钮覆盖层 */}
              {!playingVideo && videoData.url && (
                <div
                  className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ zIndex: 10 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setPlayingVideo(true)
                    if (videoRef.current) {
                      videoRef.current.play()
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('playVideo')}
                  title={t('playVideo')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setPlayingVideo(true)
                      if (videoRef.current) {
                        videoRef.current.play()
                      }
                    }
                  }}
                >
                  <div className="bg-gradient-to-br from-green-400/20 to-cyan-400/20 rounded-full p-3 sm:p-4 backdrop-blur-sm hover:from-green-400/30 hover:to-cyan-400/30 transition-all duration-300 shadow-lg shadow-green-400/20 border border-green-400/30">
                    <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white" aria-hidden="true" />
                  </div>
                </div>
              )}

              {/* 视频加载状态 */}
              {isLoading && videoData.url && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-2 border-green-400 border-t-transparent mb-2" />
                    <p className="text-xs sm:text-sm text-green-400">{t('loadingVideo')}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 底部信息区域 - 响应式优化 */}
        <div 
          className="p-3 sm:p-3 flex-shrink-0 bg-black/90 border-t border-white/5"
        >
          <p className="text-sm sm:text-sm text-gray-300 line-clamp-2 mb-2.5 sm:mb-2.5 leading-relaxed font-medium">
            {videoData.prompt || t('loadingPrompt')}
          </p>
          <div className="flex justify-between items-center gap-2.5">
            <div className="flex items-center space-x-2.5 sm:space-x-2.5 min-w-0 flex-1">
              <div className="w-6 h-6 sm:w-5 sm:h-5 rounded-full bg-gradient-to-r from-green-400 to-cyan-400 flex items-center justify-center text-white text-xs sm:text-xs font-bold flex-shrink-0 shadow-md">
                {item.email ? item.email[0].toUpperCase() : '?'}
              </div>
              <div className="text-xs sm:text-xs text-gray-400 truncate font-medium">
                {convertTimestamp(item.created_at)}
              </div>
            </div>
            
            {/* 模型和类型信息 + 下载按钮 */}
            <div className="flex items-center space-x-2 sm:space-x-2 flex-shrink-0">
              {(item.originalType || item.type) && (
                <span className="text-xs sm:text-[10px] px-2.5 sm:px-2 py-1.5 sm:py-1 rounded-md bg-green-400/20 text-green-300 font-semibold border border-green-400/30 whitespace-nowrap shadow-sm">
                  {(() => {
                    const displayType = item.originalType || item.type;
                    return displayType === 'pixverse' ? t('fast') : displayType;
                  })()}
                </span>
              )}
              {item.model && (
                <span className="text-xs sm:text-[10px] px-2.5 sm:px-2 py-1.5 sm:py-1 rounded-md bg-gray-700/80 text-gray-200 font-semibold border border-gray-600/50 whitespace-nowrap shadow-sm">
                  {item.model}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 删除确认弹窗 - 现代化设计 */}
        {showDeleteConfirm && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              // 点击背景关闭弹窗
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false)
              }
            }}
          >
            <div 
              className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl px-5 sm:px-8 py-8 sm:py-12 max-w-sm sm:max-w-md w-full mx-4 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.4),0_10px_10px_-5px_rgba(0,0,0,0.04)] backdrop-blur-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6 sm:mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-5 rounded-full bg-gradient-to-br from-red-500/20 to-pink-500/20 flex items-center justify-center border border-red-400/30">
                  <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">{t('deleteVideoTitle')}</h3>
                <p className="text-gray-400 text-sm leading-relaxed px-2 sm:px-0">
                  {t('deleteVideoDescription')}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 text-sm font-semibold text-gray-300 hover:text-white bg-gray-800/50 hover:bg-gray-700/50 rounded-2xl border border-gray-600/50 hover:border-gray-500/50 transition-all duration-200 touch-manipulation min-h-[48px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowDeleteConfirm(false)
                  }}
                  disabled={isDeleting}
                >
                  {t('cancel')}
                </button>
                <button
                  className="flex-1 px-4 py-3 text-sm font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-2xl transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50 touch-manipulation min-h-[48px] shadow-lg shadow-red-500/25 whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteConfirm()
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span className="truncate">{t('delete')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 可见性确认弹窗 - 现代化设计 */}
        {showVisibilityConfirm && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              // 点击背景关闭弹窗
              if (e.target === e.currentTarget) {
                setShowVisibilityConfirm(false)
              }
            }}
          >
            <div 
              className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl px-5 sm:px-8 py-8 sm:py-12 max-w-sm sm:max-w-md w-full mx-4 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.4),0_10px_10px_-5px_rgba(0,0,0,0.04)] backdrop-blur-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6 sm:mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-5 rounded-full bg-gradient-to-br from-green-400/20 to-cyan-400/20 flex items-center justify-center border border-green-400/30">
                  {item.is_private ? (
                    <Globe className="w-7 h-7 sm:w-8 sm:h-8 text-green-400" />
                  ) : (
                    <Lock className="w-7 h-7 sm:w-8 sm:h-8 text-orange-400" />
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">
                  {t('changeVisibilityTitle')}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed px-2 sm:px-0">
                  {item.is_private 
                    ? t('makePublicDescription')
                    : t('makePrivateDescription')
                  }
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 text-sm font-semibold text-gray-300 hover:text-white bg-gray-800/50 hover:bg-gray-700/50 rounded-2xl border border-gray-600/50 hover:border-gray-500/50 transition-all duration-200 touch-manipulation min-h-[48px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowVisibilityConfirm(false)
                  }}
                  disabled={isTogglingVisibility}
                >
                  {t('cancel')}
                </button>
                <button
                  className="flex-1 px-4 py-3 text-sm font-semibold bg-gradient-to-r from-green-400 to-cyan-400 hover:from-green-500 hover:to-cyan-500 text-white rounded-2xl transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50 touch-manipulation min-h-[48px] shadow-lg shadow-green-400/25 whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleVisibilityConfirm()
                  }}
                  disabled={isTogglingVisibility}
                >
                  {isTogglingVisibility && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span className="truncate">
                    {item.is_private ? t('makePublic') : t('makePrivate')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        <Suspense fallback={<div>Loading...</div>}>
          <PricingModal 
            isOpen={showPricingModal} 
            onClose={() => setShowPricingModal(false)} 
          />
        </Suspense>
      </div>  
    </div>
  )
} 